/**
 * HematologyTwinPage — „Хематологична интелигентност".
 * Кислороден капацитет, железен статус, B12/фолат, ретикулоцитен отговор,
 * имунен сигнал и хемостаза — от реалните лаборатории на пациента, плюс
 * връзките към останалите близнаци (бъбрек, възпаление, метаболитен,
 * кортизол, тиреоид, дентален, гърда).
 * Данни: GET/POST /api/hematology-twin/*
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { twinLabel } from "@/lib/twinCatalog";
import { MobileTopBar } from "@/components/MobileTopBar";
import {
  ArrowLeft, ArrowRight, Droplets, AlertTriangle, Loader2, ChevronDown,
  FlaskConical, Link2,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import PhysiologyBridgeCard from "@/components/PhysiologyBridgeCard";
import BloodTrendChart from "@/components/hematology/BloodTrendChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BAND = { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };
const ORDER = ["hemoglobin", "hematocrit", "mcv", "rdw", "wbc", "neutrophil_pct",
  "lymphocyte_pct", "platelets", "ferritin", "transferrin_saturation", "b12",
  "folate", "reticulocytes"];

export default function HematologyTwinPage() {
  const { isDark } = useTheme();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [openForm, setOpenForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/hematology-twin/snapshot`)
    .then((r) => setD(r.data))
    .catch(() => toast.error("Не успях да заредя хематологичния близнак"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    const values = {};
    Object.entries(form).forEach(([k, v]) => {
      const n = parseFloat(v);
      if (!Number.isNaN(n) && n > 0) values[k] = n;
    });
    if (!Object.keys(values).length) { toast.error("Въведете поне една стойност"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/hematology-twin/labs`, { values });
      setD(data.snapshot);
      setForm({});
      setOpenForm(false);
      toast.success(`Записани ${data.saved} показателя`);
    } catch { toast.error("Записът не мина"); }
    finally { setSaving(false); }
  };

  const border = isDark ? "border-white/10" : "border-gray-200";
  const card = isDark ? "bg-[hsl(223,47%,11%)] border border-white/5"
                      : "bg-white border border-gray-100 shadow-sm";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-[#1A1A2E]"}`}
      data-testid="hematology-twin-page">
      <MobileTopBar title="Кръв" fallbackTo="/dashboard" testid="hematology-twin-mobile-topbar" />
      <SEO title={twinLabel("hematology")}
        description="Кислороден капацитет, желязо, B12 и фолат, ретикулоцити, имунен сигнал и хемостаза."
        path="/dashboard/hematology-twin" />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs font-bold opacity-70 hover:opacity-100"
          data-testid="hema-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold inline-flex items-center gap-2">
              <Droplets className="w-6 h-6 text-rose-500" /> {twinLabel("hematology")}
            </h1>
            <p className="text-sm opacity-70 mt-1 max-w-2xl">
              Кръвта е най-евтиният системен сензор — показва дефицити,
              възпаление, загуба на кръв и бъбречен дефицит на еритропоетин
              наведнъж.
            </p>
          </div>
          {d?.score != null && (
            <div className="text-right" data-testid="hema-score-block">
              <div className="text-4xl font-extrabold tabular-nums"
                style={{ color: BAND[d.band] || "#0EA5E9" }} data-testid="hema-score">
                {d.score}
              </div>
              <div className="text-[11px] opacity-70">от 100</div>
            </div>
          )}
        </div>

        {!d && (
          <div className="mt-8 flex items-center gap-2 text-sm opacity-70">
            <Loader2 className="w-4 h-4 animate-spin" /> Зареждам…
          </div>
        )}

        {d && (
          <>
            {d.pattern && (
              <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="hema-pattern">
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
                  Модел от Вашите показатели
                </div>
                <div className="text-base font-bold mt-0.5">{d.pattern.title_bg}</div>
                <p className="text-sm opacity-80 mt-1">{d.pattern.text_bg}</p>
              </div>
            )}

            {/* ── въвеждане на кръвна картина ───────────────────────── */}
            <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="hema-labs-card">
              <button type="button" onClick={() => setOpenForm((v) => !v)}
                className="w-full flex items-center justify-between gap-2 text-left"
                data-testid="hema-form-toggle">
                <span className="text-sm font-bold inline-flex items-center gap-1.5">
                  <FlaskConical className="w-4 h-4 text-sky-500" />
                  Кръвна картина и железен статус
                </span>
                <ChevronDown className={`w-4 h-4 opacity-50 transition-transform ${openForm ? "rotate-180" : ""}`} />
              </button>
              <p className="text-[11px] opacity-70 mt-1">
                Показателите се четат автоматично от вече въведените
                лаборатории. Тук допълвате липсващото — записаното влиза в
                общите лаборатории и го виждат и другите близнаци.
              </p>

              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {ORDER.filter((k) => d.labs_meta[k]).map((k) => (
                  <div key={k} className={`p-2 rounded-lg border ${border}`}
                    data-testid={`hema-lab-${k}`}>
                    <div className="text-[10px] opacity-60 truncate">
                      {d.labs_meta[k].label_bg}
                    </div>
                    <div className="text-sm font-bold tabular-nums">
                      {d.labs[k] != null ? `${d.labs[k]} ` : "—"}
                      <span className="text-[9px] font-normal opacity-60">
                        {d.labs[k] != null ? d.labs_meta[k].unit : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {openForm && (
                <div className="mt-3" data-testid="hema-form">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {ORDER.filter((k) => d.labs_meta[k]).map((k) => (
                      <label key={k} className="text-[11px] opacity-80">
                        {d.labs_meta[k].label_bg}
                        <span className="opacity-50"> ({d.labs_meta[k].unit})</span>
                        <input type="number" step="0.1" inputMode="decimal"
                          value={form[k] ?? ""}
                          placeholder={d.labs[k] != null ? String(d.labs[k]) : ""}
                          onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                          className={`mt-0.5 w-full px-2 py-1 rounded-lg border bg-transparent text-sm ${border}`}
                          data-testid={`hema-input-${k}`} />
                      </label>
                    ))}
                  </div>
                  <button type="button" onClick={save} disabled={saving}
                    className="mt-3 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="hema-save-btn">
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Запиши
                  </button>
                </div>
              )}
            </div>

            <div className="mt-4">
              <BloodTrendChart isDark={isDark} cardCls={`p-4 rounded-2xl ${card}`} />
            </div>

            {/* ── под-оценки ─────────────────────────────────────────── */}
            {d.sub_scores?.length > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="hema-subscores">
                {d.sub_scores.map((s) => (
                  <div key={s.key} className={`p-4 rounded-2xl ${card}`}
                    data-testid={`hema-sub-${s.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-bold">{s.title_bg}</div>
                      <div className="text-xl font-extrabold tabular-nums"
                        style={{ color: s.score >= 75 ? BAND.green : s.score >= 55 ? BAND.yellow : s.score >= 35 ? BAND.orange : BAND.red }}>
                        {s.score}
                      </div>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full overflow-hidden"
                      style={{ background: isDark ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.06)" }}>
                      <div className="h-full rounded-full"
                        style={{ width: `${s.score}%`, background: s.score >= 75 ? BAND.green : s.score >= 55 ? BAND.yellow : s.score >= 35 ? BAND.orange : BAND.red }} />
                    </div>
                    <p className="text-[12px] opacity-80 mt-2">{s.text_bg}</p>
                  </div>
                ))}
              </div>
            )}

            {/* ── връзки към останалите близнаци ─────────────────────── */}
            {d.links?.length > 0 && (
              <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="hema-links">
                <div className="text-sm font-bold inline-flex items-center gap-1.5">
                  <Link2 className="w-4 h-4 text-violet-500" />
                  Какво значи това за другите близнаци
                </div>
                <div className="mt-2 space-y-2">
                  {d.links.map((l, i) => (
                    <div key={i}
                      className={`p-3 rounded-xl border ${l.urgency === "high"
                        ? "border-rose-500/40 bg-rose-500/10" : border}`}
                      data-testid={`hema-link-${l.twin}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {l.urgency === "high" && <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />}
                        <span className="text-[13px] font-bold">{l.title_bg}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-500/15">
                          {l.twin_bg}
                        </span>
                      </div>
                      <p className="text-[12px] opacity-80 mt-1">{l.why_bg}</p>
                      <p className="text-[12px] font-medium mt-1">{l.action_bg}</p>
                      <Link to={l.route}
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-violet-600 dark:text-violet-300"
                        data-testid={`hema-link-cta-${l.twin}`}>
                        Отвори {l.twin_bg} <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── действия ───────────────────────────────────────────── */}
            {d.actions?.length > 0 && (
              <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="hema-actions">
                <div className="text-sm font-bold">Следващи стъпки</div>
                <ul className="mt-2 space-y-2">
                  {d.actions.map((a, i) => (
                    <li key={i} className="text-[12px]">
                      <span className="font-bold">{a.title_bg}</span>
                      <span className="opacity-80"> — {a.why_bg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── липсващи данни ─────────────────────────────────────── */}
            {d.gaps?.length > 0 && (
              <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="hema-gaps">
                <div className="text-sm font-bold">Какво не мога да преценя без тези показатели</div>
                <ul className="mt-2 space-y-1.5">
                  {d.gaps.map((g) => (
                    <li key={g.key} className="text-[12px]" data-testid={`hema-gap-${g.key}`}>
                      <span className="font-bold">{g.label_bg}</span>
                      <span className="opacity-80"> — {g.text_bg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4">
              <PhysiologyBridgeCard focus="blood" isDark={isDark} cardCls={`p-4 rounded-2xl ${card}`} />
            </div>

            <p className="text-[10px] opacity-50 mt-4">{d.note_bg}</p>
          </>
        )}
      </div>
    </div>
  );
}
